import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const UserSessionContext = createContext(null);

/**
 * Exposes AuthContext user as userSession (legacy shape: id, email, first_name, last_name, role)
 * so Header, NavigationDrawer, SharePointContext etc. keep working.
 */
function mapUserToUserSession(user) {
  if (!user) return null;
  const dn = (user.displayName || '').trim();
  const parts = (dn || user.email || '').split(/\s+/).filter(Boolean);
  const first_name = parts[0] || '';
  const last_name = parts.length > 1 ? parts.slice(1).join(' ') : '';
  const displayName =
    dn || [first_name, last_name].filter(Boolean).join(' ').trim() || user.email || '';
  return {
    id: user.userId,
    userId: user.userId,
    email: user.email,
    displayName,
    effectiveEmail: user.effectiveEmail || user.email,
    impersonating: Boolean(user.impersonating),
    impersonatedEmail: user.impersonatedEmail || null,
    impersonatedConfigId:
      user.impersonatedConfigId != null && !Number.isNaN(parseInt(user.impersonatedConfigId, 10))
        ? parseInt(user.impersonatedConfigId, 10)
        : null,
    mustChangePassword: Boolean(user.mustChangePassword),
    first_name,
    last_name,
    role: user.roles && user.roles[0] ? user.roles[0] : null,
    roles: user.roles || [],
    isActive: user.isActive,
    lastLoginAt: user.lastLoginAt
  };
}

export const UserSessionProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const userSession = useMemo(() => mapUserToUserSession(user), [user]);

  return (
    <UserSessionContext.Provider value={{ userSession, loading }}>
      {children}
    </UserSessionContext.Provider>
  );
};

export const useUserSession = () => {
  const ctx = useContext(UserSessionContext);
  if (!ctx) throw new Error('useUserSession must be used within UserSessionProvider');
  return ctx;
};
